import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "reppi-color-scheme";
const LIGHT = "light";
const DARK = "dark";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

/* Reads the saved choice, falling back to the OS preference. localStorage
   throws in Safari private mode, so every access is guarded. */
const readStoredScheme = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === LIGHT || stored === DARK) {
      return stored;
    }
  } catch {
    // Storage unavailable - fall through to the OS preference.
  }

  try {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return DARK;
    }
  } catch {
    // matchMedia unavailable - fall through to light.
  }

  return LIGHT;
};

export const ThemeProvider = ({ children }) => {
  const [colorScheme, setColorScheme] = useState(readStoredScheme);

  /* The same attribute the inline script in index.html sets before first
     paint, kept in sync here so a toggle repaints the whole app at once. */
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", colorScheme);
  }, [colorScheme]);

  const setScheme = useCallback(scheme => {
    const next = scheme === DARK ? DARK : LIGHT;
    setColorScheme(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage unavailable - the choice just will not survive a reload.
    }
  }, []);

  const toggleColorScheme = useCallback(() => {
    setScheme(colorScheme === DARK ? LIGHT : DARK);
  }, [colorScheme, setScheme]);

  const value = useMemo(
    () => ({
      colorScheme,
      isDark: colorScheme === DARK,
      setColorScheme: setScheme,
      toggleColorScheme,
    }),
    [colorScheme, setScheme, toggleColorScheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
